import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolderPlus,
  faFolderOpen,
  faTrash,
  faImages,
  faImage,
  faSave,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import MediaSelector from "./MediaSelector";
import MediaFilePreview from "./MediaFilePreview";
import { createMediaGalleryFoldersInstance } from "../../../Models/MediaGalleryFolders";
import "./Media.css";

const MediaGalleryFoldersAdmin = () => {
  const [foldersInstance, setFoldersInstance] = useState(
    createMediaGalleryFoldersInstance()
  );
  const [loading, setLoading] = useState(false);
  const [isAddingFolder, setIsAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [expandedFolderId, setExpandedFolderId] = useState(null);

  const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
  const [mediaSelectorConfig, setMediaSelectorConfig] = useState({
    folderId: null,
    mode: "images", // 'images' | 'main'
    isMultiple: true,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await foldersInstance.load();
        // force re-render with loaded instance
        setFoldersInstance(foldersInstance);
      } catch (error) {
        console.error("Failed to load media folders:", error);
        toast.error("Failed to load media folders");
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddFolderClick = () => {
    setIsAddingFolder(true);
    setNewFolderName("");
  };

  const handleCreateFolder = () => {
    const name = newFolderName.trim();
    if (!name) {
      toast.warning("Folder name is required");
      return;
    }
    foldersInstance.addFolder({ folderName: name });
    // Create a new instance to force React re-render
    const newInstance = createMediaGalleryFoldersInstance();
    newInstance.setData(foldersInstance.getData());
    setFoldersInstance(newInstance);
    setIsAddingFolder(false);
    setNewFolderName("");
    toast.success("Folder created");
  };

  const handleDeleteFolder = (folderId) => {
    foldersInstance.removeFolder(folderId);
    // Create a new instance to force React re-render
    const newInstance = createMediaGalleryFoldersInstance();
    newInstance.setData(foldersInstance.getData());
    setFoldersInstance(newInstance);
    if (expandedFolderId === folderId) {
      setExpandedFolderId(null);
    }
    toast.info("Folder removed");
  };

  const handleRemoveImage = (folderId, imageUrl) => {
    foldersInstance.removeImageFromFolder(folderId, imageUrl);
    // Create a new instance to force React re-render
    const newInstance = createMediaGalleryFoldersInstance();
    newInstance.setData(foldersInstance.getData());
    setFoldersInstance(newInstance);
  };

  const openMediaSelectorForFolderImages = (folderId) => {
    setMediaSelectorConfig({
      folderId,
      mode: "images",
      isMultiple: true,
    });
    setIsMediaSelectorOpen(true);
  };

  const openMediaSelectorForFolderMainImage = (folderId) => {
    setMediaSelectorConfig({
      folderId,
      mode: "main",
      isMultiple: false,
    });
    setIsMediaSelectorOpen(true);
  };

  const handleMediaSelect = (selectedPaths) => {
    const { folderId, mode, isMultiple } = mediaSelectorConfig;
    if (!folderId) {
      setIsMediaSelectorOpen(false);
      return;
    }

    if (mode === "images") {
      const imagesArray = Array.isArray(selectedPaths)
        ? selectedPaths
        : [selectedPaths];
      foldersInstance.addImagesToFolder(folderId, imagesArray);
      // Create a new instance to force React re-render
      const newInstance = createMediaGalleryFoldersInstance();
      newInstance.setData(foldersInstance.getData());
      setFoldersInstance(newInstance);
    } else if (mode === "main") {
      const mainImage = Array.isArray(selectedPaths)
        ? selectedPaths[0]
        : selectedPaths;
      if (mainImage) {
        foldersInstance.setFolderMainImage(folderId, mainImage);
        // Create a new instance to force React re-render
        const newInstance = createMediaGalleryFoldersInstance();
        newInstance.setData(foldersInstance.getData());
        setFoldersInstance(newInstance);
      }
    }

    setIsMediaSelectorOpen(false);
  };


  const handleVisibilityToggle = () => {
    // visibility is no longer used; keep handler as no-op for safety
  };

  const handleModeChange = (e) => {
    // mode is no longer used; keep handler as no-op for safety
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await foldersInstance.save();
      toast.success("Media folders saved successfully");
    } catch (error) {
      console.error("Failed to save media folders:", error);
      toast.error("Failed to save media folders");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  const folders = foldersInstance.getFolders();

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Media Folders</h2>

      <Row className="mb-3">
        <Col>
          <Button variant="success" onClick={handleAddFolderClick}>
            <FontAwesomeIcon icon={faFolderPlus} className="me-2" />
            Add Folder
          </Button>
        </Col>
      </Row>

      {isAddingFolder && (
        <Row className="mb-4">
          <Col md={6}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
              />
              <Button variant="primary" onClick={handleCreateFolder}>
                Create
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => setIsAddingFolder(false)}
              >
                Cancel
              </Button>
            </InputGroup>
          </Col>
        </Row>
      )}

      <Row className="g-4">
        {folders.map((folder) => (
          <Col key={folder.id} xs={12} md={6} lg={4}>
            <Card className="h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faFolderOpen} className="text-warning" />
                    <strong>{folder.folderName}</strong>
                  </div>
                  <div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteFolder(folder.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </div>

                <div className="mb-3">
                  <Form.Label>Main Media</Form.Label>
                  <div className="d-flex align-items-center gap-3">
                    {folder.folderMainImage ? (
                      <div
                        style={{
                          width: "64px",
                          height: "64px",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                          overflow: "hidden",
                        }}
                      >
                        <MediaFilePreview
                          filePath={folder.folderMainImage}
                          alt={folder.folderName}
                          asCardImg={false}
                          compact
                          allowEmbedFallback
                          imageClassName=""
                          style={{
                            width: "64px",
                            height: "64px",
                            objectFit: "cover",
                          }}
                          videoProps={{ controls: false, muted: true }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          width: "64px",
                          height: "64px",
                          borderRadius: "4px",
                          border: "1px dashed #ccc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#999",
                          fontSize: "0.8rem",
                        }}
                      >
                        No media
                      </div>
                    )}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() =>
                        openMediaSelectorForFolderMainImage(folder.id)
                      }
                    >
                      <FontAwesomeIcon icon={faImage} className="me-1" />
                      Choose Main Image
                    </Button>
                  </div>
                </div>

                <div className="mb-2 d-flex justify-content-between align-items-center">
                  <span>
                    <FontAwesomeIcon icon={faImages} className="me-1" />
                    {Array.isArray(folder.folderImages) ? folder.folderImages.length : 0}{" "}
                    media
                  </span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() =>
                      setExpandedFolderId(
                        expandedFolderId === folder.id ? null : folder.id
                      )
                    }
                  >
                    {expandedFolderId === folder.id ? (
                      <>
                        Hide <FontAwesomeIcon icon={faChevronUp} className="ms-1" />
                      </>
                    ) : (
                      <>
                        Show <FontAwesomeIcon icon={faChevronDown} className="ms-1" />
                      </>
                    )}
                  </Button>
                </div>

                {expandedFolderId === folder.id && (
                  <div className="mb-3">
                    <Row className="g-2">
                      {(folder.folderImages || []).map((mediaUrl, idx) => (
                        <Col key={`${folder.id}-${idx}`} xs={4}>
                          <div style={{ position: "relative" }}>
                            <div
                              style={{
                                width: "100%",
                                height: "80px",
                                borderRadius: "4px",
                                border: "1px solid #ddd",
                                overflow: "hidden",
                              }}
                            >
                              <MediaFilePreview
                                filePath={mediaUrl}
                                alt={`${folder.folderName}-${idx}`}
                                asCardImg={false}
                                compact
                                allowEmbedFallback
                                imageClassName=""
                                style={{
                                  width: "100%",
                                  height: "80px",
                                  objectFit: "cover",
                                }}
                                videoProps={{ controls: true, muted: true }}
                              />
                            </div>
                            <Button
                              variant="danger"
                              size="sm"
                              style={{
                                position: "absolute",
                                top: "4px",
                                right: "4px",
                                padding: "0 4px",
                              }}
                              onClick={() => handleRemoveImage(folder.id, mediaUrl)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                <div className="d-flex justify-content-end">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => openMediaSelectorForFolderImages(folder.id)}
                  >
                    <FontAwesomeIcon icon={faImages} className="me-1" />
                    Add Media
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {folders.length === 0 && (
        <Row className="mt-4">
          <Col>
            <p className="text-muted text-center mb-0">
              No folders created yet. Click &quot;Add Folder&quot; to get started.
            </p>
          </Col>
        </Row>
      )}

      <div className="mt-4 d-flex justify-content-end">
        <Button variant="primary" onClick={handleSave}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Save
        </Button>
      </div>

      <MediaSelector
        isOpen={isMediaSelectorOpen}
        onClose={() => setIsMediaSelectorOpen(false)}
        onSelect={handleMediaSelect}
        isMultiple={mediaSelectorConfig.isMultiple}
      />
    </Container>
  );
};

export default MediaGalleryFoldersAdmin;


